/**
 * Serialize the local actor database as NDJSON, so a signing-in user can keep a copy
 * of study data that is about to be replaced by their cloud account.
 *
 * Platform-neutral: the browser download lives in `download-actor-data-export.web.ts`.
 *
 * WHY NDJSON RATHER THAN A `.db` FILE
 * -----------------------------------
 * The Turso engine's on-disk state is the main file PLUS sidecars (`-wal`,
 * `-changes`, `-wal-revert`, `-info`); a lone `.db` is not self-consistent unless the
 * WAL has been folded in, and handing a user a file that silently lost their most
 * recent writes would be worse than no export at all. Row-level NDJSON is also the
 * only format a future importer could actually replay through a sync connection
 * (see `carry-guest-study-through-import.ts` for how sign-up carries it).
 *
 * One row per line, wrapped by a small header record:
 *
 *   {"format":"han.actor.export","version":1,"exportedAt":"…","schemaWatermark":…}
 *   {"table":"user_card","row":{ … }}
 *
 * Streaming line-by-line rather than one big JSON array matters: this runs at the
 * exact moment the user is being warned about data loss, so it must not OOM on a
 * heavy deck.
 *
 * Columns keep their RAW names (`deleted_at`, not `deletedAt`) and raw driver values
 * (`0`/`1` for booleans, ISO strings for dates) — the stable contract a re-importer
 * would need, and what the database actually holds.
 *
 * Soft-deleted rows ARE included. They are tombstones, not noise: dropping them would
 * resurrect deleted cards if the export were ever replayed.
 */
import type { AnySQLiteDatabase } from "@-/com.npmjs.expo-sqlite";
import arrayLength from "@-/com.npmjs.extra-array__length";

export interface ActorDataExport {
  ndjson: string;
  rowCount: number;
  tableCount: number;
}

export const ACTOR_EXPORT_FORMAT = "han.actor.export";
export const ACTOR_EXPORT_VERSION = 1;

/** sqlite internals + Turso bookkeeping — not user data, never exported. */
const INTERNAL_TABLE_RE = /^(?:sqlite_|turso_|__turso|__drizzle)/i;

interface SqliteMasterRow {
  name: string;
}

interface MigrationWatermarkRow {
  created_at: number | null;
}

/**
 * Read every user table out of `connection` and serialize it as NDJSON.
 *
 * Takes an already-open connection rather than a path so it works against whichever
 * database the caller means (the guest database before a sign-in, or the synced one).
 */
export async function buildActorDataExport(
  connection: AnySQLiteDatabase,
  options: { exportedAt: string },
): Promise<ActorDataExport> {
  const tables = await listUserTables(connection);
  const schemaWatermark = await readAppliedMigrationWatermark(connection);

  const lines: string[] = [
    JSON.stringify({
      exportedAt: options.exportedAt,
      format: ACTOR_EXPORT_FORMAT,
      // Lets a future importer refuse an export written by a NEWER schema than it
      // knows how to read, instead of silently mis-mapping columns.
      schemaWatermark,
      version: ACTOR_EXPORT_VERSION,
    }),
  ];

  let rowCount = 0;
  for (const table of tables) {
    // No `WHERE deleted_at IS NULL`: tombstones are part of the data.
    const rows = await connection.getAllAsync<Record<string, unknown>>(`SELECT * FROM "${table}"`);
    for (const row of rows) {
      lines.push(JSON.stringify({ row, table }));
      rowCount += 1;
    }
  }

  return {
    ndjson: `${lines.join("\n")}\n`,
    rowCount,
    tableCount: arrayLength(tables),
  };
}

/** `haniverse-export-2026-08-02.ndjson` */
export function buildActorDataExportFilename(exportedAt: string): string {
  const datePart = exportedAt.slice(0, 10);
  return `haniverse-export-${datePart}.ndjson`;
}

async function listUserTables(connection: AnySQLiteDatabase): Promise<string[]> {
  const rows = await connection.getAllAsync<SqliteMasterRow>(
    `SELECT name FROM sqlite_master WHERE type = 'table' ORDER BY name`,
  );
  return rows.map((row) => row.name).filter((name) => !INTERNAL_TABLE_RE.test(name));
}

/**
 * Highest applied migration timestamp, read from drizzle's own journal table.
 *
 * Read from the DATABASE rather than the bundled migration list so this module stays
 * independent of the migration bundle — the export describes what the file actually
 * contains, which is also the more useful thing for an importer to check against.
 * Returns `null` when the journal is absent (a database that was never migrated).
 */
async function readAppliedMigrationWatermark(
  connection: AnySQLiteDatabase,
): Promise<number | null> {
  try {
    const row = await connection.getFirstAsync<MigrationWatermarkRow>(
      `SELECT MAX(created_at) AS created_at FROM __drizzle_migrations`,
    );
    return row?.created_at ?? null;
  } catch {
    // No journal table: not an error, just an unmigrated database.
    return null;
  }
}
